import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, BarChart3 } from "lucide-react";
import { formatDatePretty } from "@/lib/dateUtils";
import { apiRequest } from "@/lib/queryClient";
import EmailReportDetailView from "@/components/admin/EmailReportDetailView";

interface Campaign {
  id: number;
  name: string;
  description?: string | null;
  status: string;
  createdAt: string;
  runs: CampaignRun[];
}

interface CampaignRun {
  runId: number;
  campaignId: number;
  runType: string;
  status: string;
  scheduledAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  recipientCount: number;
  createdAt: string;
   criteriaId?: number | null;
   criteriaName?: string | null;
}

interface Journey {
  id: number;
  name: string;
  description?: string | null;
  status: string;
  createdAt: string;
  triggerEvent?: string | null;
}

export default function EmailReports() {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [selectedJourneyId, setSelectedJourneyId] = useState<string>("");

  // Fetch campaigns with runs
  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ["/api/admin/crm/email-reports/campaigns"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/email-reports/campaigns"
      );
      return response.json();
    },
  });

  // Fetch journeys
  const { data: journeysData, isLoading: journeysLoading } = useQuery({
    queryKey: ["/api/admin/crm/email-reports/journeys"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/email-reports/journeys"
      );
      return response.json();
    },
  });

  const campaigns: Campaign[] = campaignsData?.campaigns || [];
  const journeys: Journey[] = journeysData?.journeys || [];

  // Filter to only active journeys
  const activeJourneys = journeys.filter((j) => j.status === "active");

  // Get selected campaign and run
  const selectedCampaign = campaigns.find(
    (c) => c.id.toString() === selectedCampaignId
  );
  const selectedRun = selectedCampaign?.runs.find(
    (r) => r.runId.toString() === selectedRunId
  );

  // Get selected journey
  const selectedJourney = activeJourneys.find(
    (j) => j.id.toString() === selectedJourneyId
  );

  // Reset run selection when campaign changes
  const handleCampaignChange = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setSelectedRunId("");
    setSelectedJourneyId(""); // Clear journey selection
  };

  // Reset campaign selection when journey changes
  const handleJourneyChange = (journeyId: string) => {
    setSelectedJourneyId(journeyId);
    setSelectedCampaignId(""); // Clear campaign selection
    setSelectedRunId("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6" />
            Email Reports
          </h2>
          <p className="text-muted-foreground">
            View detailed email delivery reports for campaigns and user journeys
          </p>
        </div>
      </div>

      {/* Selection Dropdowns */}
      <Card>
        <CardHeader>
          <CardTitle>Select Report</CardTitle>
          <CardDescription>
            Choose a campaign run or journey to view detailed email delivery
            metrics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Campaigns Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Campaigns</label>
              <Select
                value={selectedCampaignId}
                onValueChange={handleCampaignChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaignsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading campaigns...
                    </SelectItem>
                  ) : campaigns.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No campaigns found
                    </SelectItem>
                  ) : (
                    campaigns.map((campaign) => (
                      <SelectItem
                        key={campaign.id}
                        value={campaign.id.toString()}
                      >
                        {campaign.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>

              {/* Campaign Runs Dropdown */}
              {selectedCampaign && selectedCampaign.runs.length > 0 && (
                <Select value={selectedRunId} onValueChange={setSelectedRunId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a run" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCampaign.runs.map((run) => {
                      const dateStr = formatDatePretty(
                        run.startedAt || run.scheduledAt || run.createdAt
                      );
                      return (
                        <SelectItem
                          key={run.runId}
                          value={run.runId.toString()}
                        >
                          Run #{run.runId} ({dateStr})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Journeys Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                User Journeys (Active)
              </label>
              <Select
                value={selectedJourneyId}
                onValueChange={handleJourneyChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an active journey" />
                </SelectTrigger>
                <SelectContent>
                  {journeysLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading journeys...
                    </SelectItem>
                  ) : activeJourneys.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No active journeys found
                    </SelectItem>
                  ) : (
                    activeJourneys.map((journey) => (
                      <SelectItem
                        key={journey.id}
                        value={journey.id.toString()}
                      >
                        {journey.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail View */}
      {selectedRun && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Campaign Report: {selectedCampaign?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EmailReportDetailView
              type="campaign"
              campaignRun={{
                runId: selectedRun.runId,
                campaignId: selectedRun.campaignId,
                campaignName: selectedCampaign?.name || "",
                runType: selectedRun.runType,
                status: selectedRun.status,
                recipientCount: selectedRun.recipientCount,
                scheduledAt: selectedRun.scheduledAt,
                startedAt: selectedRun.startedAt,
                completedAt: selectedRun.completedAt,
                templateName: null,
                criteriaName: selectedRun.criteriaName || null,
                description: selectedCampaign?.description || null,
              }}
            />
          </CardContent>
        </Card>
      )}

      {selectedJourney && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Journey Report: {selectedJourney.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EmailReportDetailView
              type="journey"
              journey={{
                journeyId: selectedJourney.id,
                journeyName: selectedJourney.name,
                description: selectedJourney.description,
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!selectedRun && !selectedJourney && (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Report Selected</h3>
            <p className="text-muted-foreground">
              Select a campaign run or journey from the dropdowns above to view
              email delivery reports
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
